import { makeStyles } from '@mui/styles';
import { GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import DataGrid from '#app/Component/DataGrid';
import IconAction from '#app/Component/IconAction';
import ApiHandler, { ApiResponse } from '#app/Handler/ApiHandler';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import { formatNumberToEurCurrency } from '#app/Util/NumberTool';
import { Filters } from '#app/View/Billing/RefundList/components/Search';
import { OrderClientStateTranslation } from '#app/typings/orderClient';
import { DisputeType, DisputeTypeTranslations, DisputeListRefund } from '#app/typings/orderClientLitige';
import { Pagination, Sorting } from '#app/typings/utils';

type Props = {
  filters: Filters;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  context: 'refund' | 'reshipment';
};

type Response = ApiResponse<DisputeListRefund[]> & {
  totalItems: number;
};

const List: React.FC<Props> = ({ filters, loading, setLoading, context }) => {
  const classes = useStyles();

  const [totalItems, setTotalItems] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({ page: 0, limit: 25 });
  const [sorting, setSorting] = useState<Sorting>({ sortName: 'dealAt', sortDirection: 'desc' });
  const [orderClientLitiges, setOrderClientLitiges] = useState<DisputeListRefund[]>([]);

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: 'Référence',
      minWidth: 120,
      renderCell: ({ value, row }) => (
        <Link to={getRoutePathname('sale_order_client', { uuid: row.orderClientUuid })}>{value}</Link>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'client',
      headerName: 'Client',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'channel',
      headerName: 'Canal',
    },
    {
      field: 'orderClientState',
      headerName: 'Etat de la commande',
      valueGetter: (value) => OrderClientStateTranslation[value],
    },
    {
      field: 'type',
      headerName: 'Type de litige',
      valueGetter: (value: DisputeType) => DisputeTypeTranslations[value],
      minWidth: 135,
    },
    {
      field: 'orderCreatedAt',
      headerName: 'Date de la commande',
      valueGetter: (value) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 120,
    },
    {
      field: 'createdAt',
      headerName: 'Date de litige',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 120,
    },
    {
      field: 'askingAt',
      headerName: 'Demandé le',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 120,
    },
    {
      field: 'authorAsking',
      headerName: 'Demandé Par',
      minWidth: 140,
    },
    {
      field: 'priceHT',
      headerName: 'Montant HT',
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
    {
      field: 'priceTTC',
      headerName: 'Montant TTC',
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
    {
      field: 'dealAt',
      headerName: context === 'refund' ? 'Remboursé le' : 'Réexpédié le',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 120,
    },
    {
      field: 'author',
      headerName: context === 'refund' ? 'Remboursé Par' : 'Réexpédié Par',
      minWidth: 140,
    },
    {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      width: 70,
      getActions: ({ row }) => [
        <IconAction
          title="Visualiser"
          icon="view"
          to={getRoutePathname('client_service_order_client_litige', { uuid: row.uuid })}
        />,
      ],
    },
  ];

  const handleSearch = (): void => {
    setLoading(true);
    ApiHandler.get<DisputeListRefund[]>(
      {
        route: 'api_billing_refund_list',
        data: { context, ...filters, ...pagination, ...sorting },
      },
      (response) => {
        setOrderClientLitiges(response.data ?? []);
        setTotalItems((response as Response).totalItems ?? 0);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    handleSearch();
  }, [pagination, sorting, filters]);

  return (
    <DataGrid
      className={classes.container}
      rows={transformObjectToDataGridRowsArray<DisputeListRefund>(orderClientLitiges)}
      columns={columns}
      loading={loading}
      rowCount={totalItems}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
    />
  );
};

const useStyles = makeStyles({
  container: {
    marginTop: 15,
  },
});

export default List;
